/**
 * EmojiSubmissionsReviewPanel — admin-side moderation queue for
 * community-submitted emojis. Lives inside AdminModerationTab as a
 * 4th sub-tab.
 *
 * Flow:
 *   - Pending submissions list with submitter name + preview + label.
 *   - Approve modal: tweak label / categories / excluded_platforms
 *     before publishing → adds a real emoji to the gallery.
 *   - Reject modal: required reviewer note (shown to the submitter).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Check, X, Loader2, Smile, Calendar, ImageOff,
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EmojiSubmissionsReviewPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [acting, setActing] = useState(null);
  const [approveModal, setApproveModal] = useState(null);   // submission obj
  const [rejectModal, setRejectModal] = useState(null);     // submission obj
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });

  // Approve modal form state
  const [editLabel, setEditLabel] = useState('');
  const [editCats, setEditCats] = useState('');
  const [editExclusions, setEditExclusions] = useState('');
  const [editNote, setEditNote] = useState('');
  // Reject modal form state
  const [rejectNote, setRejectNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiFetch(`/api/admin/emojis/submissions?status=${statusFilter}`);
    setLoading(false);
    if (error) return toast.error(error);
    setItems(data?.items || []);
    if (data?.counts) setCounts(data.counts);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openApprove = (s) => {
    setApproveModal(s);
    setEditLabel(s.label || '');
    setEditCats((s.suggested_categories || []).join(', '));
    setEditExclusions('');
    setEditNote('');
  };

  const submitApprove = async () => {
    if (!approveModal) return;
    const body = {
      label: editLabel.trim(),
      categories: editCats.split(',').map((c) => c.trim()).filter(Boolean),
      excluded_platforms: editExclusions.split(',').map((c) => c.trim()).filter(Boolean),
      admin_note: editNote.trim() || null,
    };
    setActing(approveModal.submission_id);
    const { error } = await apiFetch(`/api/admin/emojis/submissions/${approveModal.submission_id}/approve`, {
      method: 'POST',
      body,
    });
    setActing(null);
    if (error) return toast.error(error);
    toast.success(`Approved "${body.label}" — added to gallery.`);
    setApproveModal(null);
    load();
  };

  const submitReject = async () => {
    if (!rejectModal || rejectNote.trim().length < 2) {
      return toast.error('Reviewer note is required (and shown to the submitter).');
    }
    setActing(rejectModal.submission_id);
    const { error } = await apiFetch(`/api/admin/emojis/submissions/${rejectModal.submission_id}/reject`, {
      method: 'POST',
      body: { admin_note: rejectNote.trim() },
    });
    setActing(null);
    if (error) return toast.error(error);
    toast.success('Submission rejected — submitter notified via the My Submissions panel.');
    setRejectModal(null);
    setRejectNote('');
    load();
  };

  return (
    <div className="space-y-3" data-testid="emoji-submissions-review-panel">
      {/* Status filter chips */}
      <div className="flex gap-2 flex-wrap" data-testid="submissions-status-filter">
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? 'default' : 'outline'}
            onClick={() => setStatusFilter(s)}
            data-testid={`submissions-filter-${s}`}
          >
            <span className="capitalize">{s}</span>
            {s in counts && counts[s] > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">{counts[s]}</Badge>
            )}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8" data-testid="empty-submissions">
          No {statusFilter === 'all' ? '' : statusFilter + ' '}submissions.
        </p>
      ) : (
        items.map((s) => (
          <Card
            key={s.submission_id}
            className={s.status === 'pending' ? 'border-amber-200/60 dark:border-amber-900/40' : 'opacity-80'}
            data-testid={`submission-${s.submission_id}`}
          >
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-20 h-20 shrink-0 border rounded bg-white flex items-center justify-center overflow-hidden">
                  {s.image_path ? (
                    <img
                      src={s.image_path.startsWith('http') ? s.image_path : `${API}${s.image_path}`}
                      alt={s.label}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <ImageOff className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Smile className="h-4 w-4 text-[#00A896] shrink-0" />
                    <p className="font-semibold truncate">{s.label}</p>
                    <Badge variant="outline" className="text-[10px] capitalize">{s.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 break-words">
                    From <b>{s.submitter_name}</b>
                    {s.submitter_email && <span className="break-all"> ({s.submitter_email})</span>}
                  </p>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Calendar className="h-3 w-3" /> {new Date(s.created_at).toLocaleString()}
                  </p>
                  {s.description && (
                    <p className="text-xs mt-2 bg-muted/40 border rounded p-2 break-words">
                      {s.description}
                    </p>
                  )}
                  {(s.suggested_categories || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {s.suggested_categories.map((c) => (
                        <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>
                      ))}
                    </div>
                  )}
                  {s.admin_note && (
                    <p className={`text-xs mt-2 break-words ${s.status === 'rejected' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                      <b>Reviewer note:</b> {s.admin_note}
                    </p>
                  )}
                </div>
              </div>

              {s.status === 'pending' && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={() => openApprove(s)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`approve-submission-${s.submission_id}`}
                  >
                    <Check className="h-4 w-4 mr-1" /> Approve &amp; publish
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejectModal(s)}
                    className="border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                    data-testid={`reject-submission-${s.submission_id}`}
                  >
                    <X className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Approve dialog */}
      <Dialog open={!!approveModal} onOpenChange={(o) => !o && setApproveModal(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="approve-submission-dialog">
          <DialogHeader>
            <DialogTitle>Approve &amp; publish emoji</DialogTitle>
            <DialogDescription>
              Tweak the label and categories before publishing. The submitter is credited automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Label</Label>
              <Input
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value.slice(0, 60))}
                maxLength={60}
                data-testid="approve-label-input"
              />
            </div>
            <div>
              <Label className="text-xs">Categories (comma-separated)</Label>
              <Input
                value={editCats}
                onChange={(e) => setEditCats(e.target.value)}
                placeholder="e.g. mood, sensory, request"
                data-testid="approve-cats-input"
              />
            </div>
            <div>
              <Label className="text-xs">Excluded platforms (comma-separated, optional)</Label>
              <Input
                value={editExclusions}
                onChange={(e) => setEditExclusions(e.target.value)}
                placeholder="e.g. twitch-28, whatsapp"
                data-testid="approve-exclusions-input"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Skip platforms where this glyph wouldn&apos;t read well at small sizes.
              </p>
            </div>
            <div>
              <Label className="text-xs">Internal note (optional, not shown to submitter)</Label>
              <Textarea
                rows={2}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value.slice(0, 500))}
                data-testid="approve-note-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => setApproveModal(null)} data-testid="approve-cancel-btn">Cancel</Button>
            <Button
              onClick={submitApprove}
              disabled={!!acting || editLabel.trim().length < 2}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="approve-confirm-btn"
            >
              {acting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Publish to gallery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectModal} onOpenChange={(o) => !o && setRejectModal(null)}>
        <DialogContent className="max-w-md" data-testid="reject-submission-dialog">
          <DialogHeader>
            <DialogTitle>Reject submission</DialogTitle>
            <DialogDescription>
              Tell the submitter why their emoji wasn&apos;t approved — this shows up in their My Submissions panel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reviewer note <span className="text-red-500">*</span></Label>
            <Textarea
              rows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value.slice(0, 500))}
              placeholder="e.g. The image is too low-resolution to scale to all platforms. Please re-submit at 512×512 px or higher."
              data-testid="reject-note-input"
            />
            <p className="text-[10px] text-muted-foreground">{rejectNote.length}/500</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" onClick={() => { setRejectModal(null); setRejectNote(''); }} data-testid="reject-cancel-btn">Cancel</Button>
            <Button
              onClick={submitReject}
              disabled={!!acting || rejectNote.trim().length < 2}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reject-confirm-btn"
            >
              {acting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
              Reject submission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmojiSubmissionsReviewPanel;
